import { authenticated, post, ResponseError } from '../network';
import { FormSecurityTokenResponse } from './FormSecurityTokenResponse';
import { CustomerDataResponse } from './CustomerDataResponse';
import { CustomerIdAndOrderIdIntegrityResponse } from './CustomerIdAndOrderIdIntegrityResponse';
import { OrderArticlesDataResponse } from './OrderArticlesDataResponse';
import { CaptchaConfigurationResponse } from './CaptchaConfigurationResponse';
import { CaptchaConfigurationRequest } from './CaptchaConfigurationRequest';
import { OrderStatusResponse } from './OrderStatusResponse';
import { FeatureFlagRequest } from './FeatureFlagRequest';
import { FeatureFlagResponse } from './FeatureFlagResponse';
import { TrackEventRequest } from './TrackEventRequest';
import { IsOrderCancellableResponse } from './IsOrderCancellableResponse';
import { OrderDataV2Response } from './OrdersDataResponseV2';
import { OrderSellerDataResponse } from './OrderSellerDataResponse';
import { Locale } from '../i18nService';

export class ContactFormConnector {
  private createDefaultErrorHandler(errorLogHeader: string, Clazz: any) {
    return (e: ResponseError | Error) => {
      console.log(errorLogHeader, e);
      return new Clazz({});
    };
  }

  getCustomerInfo(): Promise<CustomerDataResponse> {
    return authenticated()
      .get(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/customer-data`)
      .then((response) => new CustomerDataResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred when fetching customer data: ',
          CustomerDataResponse,
        ),
      );
  }

  requestFormSecurityToken(): Promise<FormSecurityTokenResponse> {
    return post(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/token`)
      .then((response: any) => new FormSecurityTokenResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred while acquiring form security token: ',
          FormSecurityTokenResponse,
        ),
      );
  }

  requestCaptchaConfiguration(
    data: CaptchaConfigurationRequest,
  ): Promise<CaptchaConfigurationResponse> {
    return post(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/captcha/check`, data)
      .then((response: any) => new CaptchaConfigurationResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred while acquiring Captcha configuration ',
          CaptchaConfigurationResponse,
        ),
      );
  }

  requestFeatureFlags(data: FeatureFlagRequest): Promise<FeatureFlagResponse[]> {
    return authenticated()
      .post(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/feature-flags`, data)
      .then((response) => response.map((item: any) => new FeatureFlagResponse(item)))
      .catch((e: ResponseError | Error) => {
        console.log('Error occurred when fetching feature flags: ', e);
        throw e;
      });
  }
  trackEvent(data: TrackEventRequest) {
    return authenticated()
      .post(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/track-event`, data)
      .catch((error) => console.log('Error occurred when tracking event', error));
  }

  getOrderStatus(orderId: string): Promise<OrderStatusResponse> {
    return authenticated()
      .get(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/orders/${orderId}/delivery-status`)
      .then((response) => new OrderStatusResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred when fetching order status: ',
          OrderStatusResponse,
        ),
      );
  }

  getIsOrderCancellable(orderId: string): Promise<IsOrderCancellableResponse> {
    return authenticated()
      .get(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v1/orders/${orderId}/cancellation`)
      .then((response) => new IsOrderCancellableResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred when fetching information if order is cancellable: ',
          IsOrderCancellableResponse,
        ),
      );
  }

  async getOrdersV2(
    page: number,
    pageSize: number,
    siteId: number,
  ): Promise<OrderDataV2Response[]> {
    try {
      const response = await authenticated().get(
        `${process.env.REACT_APP_CONTACT_FORM_API_URL}/v2/customer-orders`,
        {
          page,
          pageSize,
          siteId,
        },
      );
      return response.map((item: any) => new OrderDataV2Response(item));
    } catch (e) {
      console.log('Error occurred when fetching orders data: ', e);
      throw e;
    }
  }

  async getOrderArticlesV2(
    orderId: string,
    suborderId: string,
    language: string,
    locale: Locale,
    siteId: number,
  ): Promise<OrderArticlesDataResponse[]> {
    try {
      const response = await authenticated().get(
        `${process.env.REACT_APP_CONTACT_FORM_API_URL}/v2/orders/${orderId}/articles`,
        {
          language,
          locale,
          suborderId,
          siteId,
        },
      );
      return response.map((item: any) => new OrderArticlesDataResponse(item));
    } catch (e) {
      console.log('Error occurred when fetching orders articles data: ', e);
      throw e;
    }
  }

  async getOrderSellerDataV2(
    orderId: string,
    suborderId: string,
    siteId: number,
  ): Promise<OrderSellerDataResponse> {
    return authenticated()
      .get(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v2/orders/${orderId}/seller-data`, {
        suborderId,
        siteId,
      })
      .then((response) => new OrderSellerDataResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred when fetching order seller data: ',
          OrderSellerDataResponse,
        ),
      );
  }

  checkCustomerIdAndOrderIdIntegrityV2(
    orderId?: string,
    suborderId?: string,
  ): Promise<CustomerIdAndOrderIdIntegrityResponse> {
    return authenticated()
      .post(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v2/data-integrity/validate`, {
        orderNumber: orderId,
        suborderNumber: suborderId,
      })
      .then((response: any) => new CustomerIdAndOrderIdIntegrityResponse(response))
      .catch(
        this.createDefaultErrorHandler(
          'Error occurred making request for data check: ',
          CustomerIdAndOrderIdIntegrityResponse,
        ),
      );
  }

  validateOrderIdV2(
    orderId: string,
    suborderId: string,
  ): Promise<CustomerIdAndOrderIdIntegrityResponse> {
    return this.checkCustomerIdAndOrderIdIntegrityV2(orderId, suborderId);
  }

  submitContactFormV2(formData: FormData): Promise<any> {
    return post(`${process.env.REACT_APP_CONTACT_FORM_API_URL}/v2/contact-form`, formData, {});
  }
}
